import {Stack} from "@fluentui/react";
import {SharedColors} from "@fluentui/theme";
import {CSSProperties, FC, useCallback, useMemo, useState} from "react";
import {useDropzone} from "react-dropzone";
import {useTranslation} from "react-i18next";
import {theme} from "..";

interface IShowBlobProps {
    disabled?: boolean;
    base64Content?: string;
    base64FilePicked: (content: string) => void;
    title?: string;
}

export const Base64FilePickerComponent: FC<IShowBlobProps> = ({
                                                                  disabled,
                                                                  base64Content,
                                                                  base64FilePicked,
                                                                  title,
                                                              }) => {
    const {t} = useTranslation();
    const [loading, setLoading] = useState(false);

    function blobToBase64(blob: Blob): Promise<string> {
        return new Promise((resolve, _) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve((reader.result as string).split(",")[1]);
            reader.readAsDataURL(blob);
        });
    }

    const onDrop = useCallback(
        (acceptedFiles: File[]) => {
            if (acceptedFiles.length) {
                setLoading(true);
                acceptedFiles[0]
                    .arrayBuffer()
                    .then((arrayBuffer) => {
                        blobToBase64(new Blob([arrayBuffer])).then(base64FilePicked);
                    })
                    .finally(() => setLoading(false));
            }
        },
        [base64FilePicked],
    );

    const {
        getRootProps,
        getInputProps,
        isDragActive,
        isDragAccept,
        isDragReject,
        acceptedFiles,
        inputRef,
        rootRef,
    } = useDropzone({onDrop, noDragEventsBubbling: true, disabled: disabled});

    const baseStyle: CSSProperties = useMemo(
        () => ({
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "10px 20px",
            borderWidth: 1,
            borderRadius: 2,
            borderColor: "rgb(133, 133, 133)",
            borderStyle: "solid",
            backgroundColor: "rgb(250, 250, 250)",
            color: "rgb(136, 136, 136)",
            outline: "none",
            transition: "all 0.2s ease-in-out",
        }),
        [],
    );

    const activeStyle: CSSProperties = useMemo(
        () => ({
            borderColor: theme?.palette.themePrimary,
            boxShadow: "0 0 2px " + theme?.palette.themePrimary,
        }),
        [],
    );

    const acceptStyle: CSSProperties = useMemo(
        () => ({
            borderColor: SharedColors.green10,
            boxShadow: "0 0 2px " + SharedColors.green10,
        }),
        [],
    );

    const rejectStyle: CSSProperties = useMemo(
        () => ({
            borderColor: SharedColors.red10,
            boxShadow: "0 0 2px " + SharedColors.red10,
        }),
        [],
    );

    const style = useMemo(
        () => ({
            ...baseStyle,
            ...(isDragActive ? activeStyle : {}),
            ...(isDragAccept ? acceptStyle : {}),
            ...(isDragReject ? rejectStyle : {}),
        }),
        [isDragActive, isDragReject, isDragAccept],
    );

    const b64toBlob = (b64Data: string, contentType = "", sliceSize = 512) => {
        const byteCharacters = atob(b64Data);
        const byteArrays = [];

        for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
            const slice = byteCharacters.slice(offset, offset + sliceSize);

            const byteNumbers = new Array(slice.length);
            for (let i = 0; i < slice.length; i++) {
                byteNumbers[i] = slice.charCodeAt(i);
            }

            const byteArray = new Uint8Array(byteNumbers);
            byteArrays.push(byteArray);
        }

        const blob = new Blob(byteArrays, {type: contentType});
        return blob;
    };

    return (
        <Stack horizontal title={title}>
            {base64Content && (
                <img
                    src={URL.createObjectURL(b64toBlob(base64Content))}
                    alt={t("blobPicker.image")}
                />
            )}
            <div style={{paddingTop: 5}} title={title}>
                <div {...getRootProps({style})} title={title}>
                    <input
                        ref={inputRef}
                        {...getInputProps()}
                        disabled={loading || disabled}
                        title={title}
                    />
                    {isDragActive ? (
                        <p>{t("drop-files-for-upload")}</p>
                    ) : (
                        <p>{t("drop-some-files-or-click-to-select")}</p>
                    )}
                </div>
            </div>
        </Stack>
    );
};
